import { WorkspaceModelType } from "../../stores/WorkspaceStore";
import { logErrorToSentry } from "../../utils/errors/logErrorToSentry";

import { ItemType, Workspace } from "../../API";

import { UndoableActionGroup } from "../common/UndoableActionGroup";
import {
    UndoableUpdateWorkspaceArchivedSet,
    UndoableUpdateWorkspaceChildIdsRemove,
    UndoableUpdateWorkspaceChildIdsSet,
    UndoableUpdateWorkspaceTrashedSet
} from "./updateWorkspaceUndoLogic";
import { workspaceToWorkspaceServer } from "@stores/convertors/workspaceToWorkspaceServer";

import { updateWorkspace as updateWorkspaceGQL } from "../../graphql/mutations";
import { workspaceChangeHistory } from "../../sync/workspaceSync";
import { queryGqlApi } from "../../graphql/queryGqlApi";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";
import { DataSourceManager } from "@stores/dataSources/DataSourceManager";

export interface UpdateWorkspaceServices extends CrudServices {
    dsManager: DataSourceManager;
}

export function assertWorkspaceChildIdsAreLibraries(
    workspaceMST: WorkspaceModelType,
    childIds: string[]
) {
    const invalidIds = childIds.filter(childId => workspaceMST.items.get(childId)?.type !== ItemType.LIBRARY);
    if (invalidIds.length > 0) {
        throw new Error(`workspace.childIds can only contain LIBRARY items. Invalid ids: ${invalidIds.join(', ')}`);
    }
}

export const updateWorkspace = async (
    services: UpdateWorkspaceServices,
    workspaceId: string,
    fieldsToUpdate: Partial<Workspace> | {
        childIds?: string[],
        avatarPath?: string,
        archivedIds?: string[],
        trashedIds?: string[],
        pinnedIds?: string[],
    }) => {
    /* todo ensure real-time sync works */
    const updateTimeISO = (new Date()).toISOString();
    const currentWorkspaceDB = services.dsManager.workspacesDS.allRows.find(wsRow => wsRow.id === workspaceId);
    if (!currentWorkspaceDB) {
        throw new Error('ws not found for edit');
    }
    workspaceChangeHistory.pinStep(updateTimeISO);

    const queryResult = await queryGqlApi(
        updateWorkspaceGQL,
        {
            input: {
                id: workspaceId,
                version: currentWorkspaceDB.version,
                ...workspaceToWorkspaceServer({
                    ...fieldsToUpdate,
                    updatedAt: updateTimeISO
                }),
            }
        },
        services.dataClientService,
        services.authService,
        'pgServer'
    );
}

export function updateWorkspaceChildIdsSet(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childIds: string[],
    dbChangeQueue: Promise<any> = Promise.resolve(),
    undoableActionGroup?: UndoableActionGroup
) {
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UndoableUpdateWorkspaceChildIdsSet(services, workspaceMST, childIds, dbChangeQueue);
        return undoableActionGroup.add(undoableAction);
    } else {
        return _updateWorkspaceChildIdsSet(services, workspaceMST, childIds, dbChangeQueue);
    }
}

export function _updateWorkspaceChildIdsSet(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childIds: string[],
    dbChangeQueue: Promise<any> = Promise.resolve()
) {
    assertWorkspaceChildIdsAreLibraries(workspaceMST, childIds);
    workspaceMST.setChildIds(childIds);
    if ([...new Set(workspaceMST!.childIds)].length !== workspaceMST.childIds.length) {
        console.warn('Duplicate child ids detected');
    }

    return dbChangeQueue.then(() => updateWorkspace(services, workspaceMST.id, {
        childIds: childIds,
    }));
}

export function updateWorkspaceArchivedIdsSet(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    archivedIds: string[],
    dbChangeQueue: Promise<any> = Promise.resolve(),
    undoableActionGroup?: UndoableActionGroup
) {
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UndoableUpdateWorkspaceArchivedSet(services, workspaceMST, archivedIds, dbChangeQueue);
        return undoableActionGroup.add(undoableAction);
    } else {
        return _updateWorkspaceArchivedIdsSet(services, workspaceMST, archivedIds, dbChangeQueue);
    }
}

export function _updateWorkspaceArchivedIdsSet(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    archivedIds: string[],
    dbChangeQueue: Promise<any> = Promise.resolve()
) {
    workspaceMST.setChildArchivedIds(archivedIds);
    if ([...new Set(workspaceMST!.archivedIds)].length !== workspaceMST.archivedIds.length) {
        console.warn('Duplicate archived ids detected');
    }

    return dbChangeQueue.then(() => updateWorkspace(services, workspaceMST.id, {
        archivedIds: archivedIds,
    }).catch(logErrorToSentry));
}

export function updateWorkspaceTrashedIdsSet(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    trashedIds: string[],
    dbChangeQueue: Promise<any> = Promise.resolve(),
    undoableActionGroup?: UndoableActionGroup
) {
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UndoableUpdateWorkspaceTrashedSet(services, workspaceMST, trashedIds, dbChangeQueue);
        return undoableActionGroup.add(undoableAction);
    } else {
        return _updateWorkspaceTrashedIdsSet(services, workspaceMST, trashedIds, dbChangeQueue);
    }
}

export function _updateWorkspaceTrashedIdsSet(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    trashedIds: string[],
    dbChangeQueue: Promise<any> = Promise.resolve()
) {
    workspaceMST.setChildTrashIds(trashedIds);
    if ([...new Set(workspaceMST!.trashedIds)].length !== workspaceMST.trashedIds.length) {
        console.warn('Duplicate trashed ids detected');
    }

    return dbChangeQueue.then(() => updateWorkspace(services, workspaceMST.id, {
        trashedIds: trashedIds,
    }).catch(logErrorToSentry));
}

export function updateWorkspaceChildIdsAdd(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string,
    index?: number
) {
    assertWorkspaceChildIdsAreLibraries(workspaceMST, [childId]);
    if (workspaceMST.childIds.indexOf(childId) > -1) {
        console.warn('attempt to add duplicate child id');
        return Promise.resolve();
    }
    if (workspaceMST.childIds.includes(childId)) {
        return Promise.resolve();
    }
    workspaceMST.addChild(childId, index);

    return updateWorkspace(services, workspaceMST.id, {
        childIds: workspaceMST.childIds.toJSON(),
    }).catch(logErrorToSentry);
}

export function updateWorkspaceChildIdsRemove(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string,
    undoableActionGroup?: UndoableActionGroup,
) {
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UndoableUpdateWorkspaceChildIdsRemove(services, workspaceMST, childId);
        return undoableActionGroup.add(undoableAction);
    } else {
        return _updateWorkspaceChildIdsRemove(services, workspaceMST, childId);
    }
}

export function _updateWorkspaceChildIdsRemove(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (!workspaceMST.childIds.includes(childId)) {
        console.warn('no such child', childId);
        return Promise.resolve();
    }
    workspaceMST.removeChild(childId);

    return updateWorkspace(services, workspaceMST.id, {
        childIds: workspaceMST.childIds.toJSON()
    }).catch(logErrorToSentry);
}

export function updateWorkspaceChildIdsMoveToTrash(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (workspaceMST.trashedIds.includes(childId)) {
        console.warn('item already in trash');
        return Promise.resolve();
    }

    workspaceMST.removeChild(childId);
    workspaceMST.addChildTrash(childId);

    return updateWorkspace(services, workspaceMST.id, {
        childIds: workspaceMST.childIds.toJSON(),
        trashedIds: workspaceMST.trashedIds.toJSON(),
    });
}

export function updateWorkspaceTrashedIdsMoveToTrashFromArchive(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (workspaceMST.trashedIds.includes(childId)) {
        console.warn('item already in trash');
        return Promise.resolve();
    }

    workspaceMST.removeChildArchived(childId);
    workspaceMST.addChildTrash(childId);

    return updateWorkspace(services, workspaceMST.id, {
        archivedIds: workspaceMST.archivedIds.toJSON(),
        trashedIds: workspaceMST.trashedIds.toJSON(),
    });
}

export function updateWorkspaceTrashedIdsAdd(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (workspaceMST.trashedIds.includes(childId)) {
        return Promise.resolve();
    }
    workspaceMST.addChildTrash(childId);

    return updateWorkspace(services, workspaceMST.id, {
        trashedIds: workspaceMST.trashedIds.toJSON(),
    }).catch(logErrorToSentry);
}

export function updateWorkspaceChildIdsMoveToArchive(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (workspaceMST.archivedIds.includes(childId)) {
        console.warn('item already in archive');
        return Promise.resolve();
    }

    workspaceMST.removeChild(childId);
    workspaceMST.addChildArchived(childId);

    return updateWorkspace(services, workspaceMST.id, {
        childIds: workspaceMST.childIds.toJSON(),
        archivedIds: workspaceMST.archivedIds.toJSON(),
    });
}

export function updateWorkspaceChildIdsRestoreFromArchive(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    assertWorkspaceChildIdsAreLibraries(workspaceMST, [childId]);
    if (!workspaceMST.archivedIds.includes(childId)) {
        console.warn('item is not in archive');
        return Promise.resolve();
    }

    workspaceMST.removeChildArchived(childId);
    workspaceMST.addChild(childId);

    return updateWorkspace(services, workspaceMST.id, {
        archivedIds: workspaceMST.archivedIds.toJSON(),
        childIds: workspaceMST.childIds.toJSON(),
    });
}

export function updateWorkspaceArchivedIdsAdd(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (workspaceMST.archivedIds.includes(childId)) {
        return Promise.resolve();
    }
    workspaceMST.addChildArchived(childId);

    return updateWorkspace(services, workspaceMST.id, {
        archivedIds: workspaceMST.archivedIds.toJSON(),
    }).catch(logErrorToSentry);
}

export function updateWorkspaceArchivedIdsRemove(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (!workspaceMST.archivedIds.includes(childId)) {
        return Promise.resolve();
    }
    workspaceMST.removeChildArchived(childId);

    return updateWorkspace(services, workspaceMST.id, {
        archivedIds: workspaceMST.archivedIds.toJSON(),
    }).catch(logErrorToSentry);
}


export function updateWorkspaceChildIdsRestoreFromTrash(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    assertWorkspaceChildIdsAreLibraries(workspaceMST, [childId]);
    if (workspaceMST.childIds.includes(childId) && !workspaceMST.trashedIds.includes(childId)) {
        console.warn('item already in');
        return Promise.resolve();
    } else if (workspaceMST.childIds.includes(childId)) {
        return updateWorkspaceTrashedIdsRemove(services, workspaceMST, childId);
    }

    workspaceMST.addChild(childId);
    workspaceMST.removeChildTrash(childId);

    return updateWorkspace(services, workspaceMST.id, {
        trashedIds: workspaceMST.trashedIds.toJSON(),
        childIds: workspaceMST.childIds.toJSON(),
    });
}


export function updateWorkspaceTrashedIdsRemove(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    childId: string
) {
    if (!workspaceMST.trashedIds.includes(childId)) {
        console.warn('no such child in trash', childId);
        return Promise.resolve();
    }
    workspaceMST.removeChildTrash(childId);

    return updateWorkspace(services, workspaceMST.id, {
        trashedIds: workspaceMST.trashedIds.toJSON(),
    });
}

export interface SlugOrName {
    name?: string;
    slug?: string;
}

export async function updateWorkspaceNameOrSlug(
    services: UpdateWorkspaceServices,
    workspaceMST: WorkspaceModelType,
    changedFields: SlugOrName
) {
    const updated: any = {};
    if (changedFields.name) {
        workspaceMST.setName(changedFields.name);
        updated.name = changedFields.name;
    }
    if (changedFields.slug) {
        workspaceMST.setSlug(changedFields.slug);
        updated.slug = changedFields.slug;
    }

    await updateWorkspace(services, workspaceMST.id, updated);
}
